import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LocaleCodes, SITE_LOCALES } from '@medable/i18n-supported-locales';
import { Locale } from 'libs.i18n-core';
import { Button, H1, SetLocaleContext, Spinner, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { ApiClientService, AuthContext, AuthEndpoint, formatLocaleOption, LocalesDropdown, useApiClient, } from 'libs.react_internal';
import { setStoredPseudoLocale } from '../../helpers/pseudo_locale_storage.helpers';
export const WelcomePage = ({ onHandleNextStep }) => {
    const translate = useLocalizeMessage();
    const { user, refreshIdentity } = useContext(AuthContext);
    const setLocale = useContext(SetLocaleContext);
    const iamClient = useApiClient(ApiClientService.IAM);
    const { addNotification } = useToastNotification();
    const updateLocaleTimeoutRef = useRef(null);
    const [userLocale, setUserLocale] = useState(formatLocaleOption((user?.locale || ''), user?.locale || ''));
    const [isLoading, setIsLoading, setIsNotLoading] = useBooleanState(false);
    const handleGettingStarted = () => onHandleNextStep();
    const handleUpdateLocale = async (selectedLocale) => {
        const currentUserLocale = user?.locale?.replace(/_/g, '-');
        if (selectedLocale.value === currentUserLocale) {
            return;
        }
        try {
            setIsLoading();
            await iamClient.put(`${AuthEndpoint.IDENTITIES}/me`, {
                locale: selectedLocale.value,
            });
            setStoredPseudoLocale(null);
            await refreshIdentity();
            const enumKey = selectedLocale.value.replace(/-/g, '_');
            setLocale?.(Locale[enumKey]);
            addNotification({
                type: 'success',
                title: translate('Locale updated'),
                subtitle: translate('Your locale is updated successfully.'),
            });
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setIsNotLoading();
        }
    };
    const handleLocaleChange = useCallback((value) => {
        setUserLocale(value);
        if (updateLocaleTimeoutRef.current) {
            clearTimeout(updateLocaleTimeoutRef.current);
        }
        updateLocaleTimeoutRef.current = setTimeout(() => handleUpdateLocale(value), 500);
    }, [handleUpdateLocale]);
    useEffect(() => {
        setUserLocale(formatLocaleOption((user?.locale || ''), user?.locale || ''));
    }, [user?.locale]);
    useEffect(() => {
        return () => {
            if (updateLocaleTimeoutRef.current) {
                clearTimeout(updateLocaleTimeoutRef.current);
                updateLocaleTimeoutRef.current = null;
            }
        };
    }, []);
    return (_jsx("div", { className: 'pl-20 mt-[30vh]', children: _jsxs("div", { className: 'max-w-[600px] mb-10', children: [_jsxs("div", { className: 'flex w-full items-center mb-4 gap-4', children: [_jsx("div", { className: 'w-[380px]', children: _jsx(LocalesDropdown, { locales: [LocaleCodes.en_US, ...SITE_LOCALES], label: '', value: userLocale, currentLocale: user?.locale || '', onChange: handleLocaleChange }) }), isLoading && _jsx(Spinner, {})] }), _jsx(H1, { className: '', children: `${translate('label.salutation', { name: user?.first ?? '' })}` }), _jsx("p", { className: 'max-w-[550px] text-neutral-400 mb-4', children: translate('info.lumos_training.welcome_title') }), _jsx("p", { className: 'max-w-[550px] text-neutral-500 mb-4', children: translate('info.lumos_training.welcome_description') }), _jsx(Button, { label: translate('info.lumos_training.start_button'), variant: 'primary', onClick: handleGettingStarted })] }) }));
};
